import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { format } from 'date-fns';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { IExerciseOption } from '../../../types/interfaces/exercise-option';
import { ITraining } from '../../../types/interfaces/training';
import { useStyles } from './AppExerciseLineChart.styles';

export interface IChartDataPoint {
  date: string;
  planned?: number;
  result?: number;
  planned2?: number;
  result2?: number;
  showLabel?: boolean;
}

export interface IAppExerciseLineChartProps {
  trainings: ITraining[];
  exerciseId: string;
  selectedOption: IExerciseOption;
  splitBySide: boolean;
  height?: number;
}

export default function AppExerciseLineChart({
  trainings,
  exerciseId,
  selectedOption,
  splitBySide,
  height = 400,
}: IAppExerciseLineChartProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const { classes } = useStyles(theme);

  const chartData = useMemo<IChartDataPoint[]>(() => {
    if (!trainings || trainings.length === 0 || !selectedOption) {
      return [];
    }

    const chartPoints: IChartDataPoint[] = [];

    const sortedTrainings = [...trainings].sort((a, b) => {
      if (!a.startDate || !b.startDate) return 0;
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA.getTime() - dateB.getTime();
    });

    sortedTrainings.forEach((training) => {
      if (!training.startDate) return;

      const trainingDate = format(new Date(training.startDate), 'dd.MM.yyyy');

      training.trainingSets?.forEach((trainingSet) => {
        if (trainingSet.exerciseId !== exerciseId) return;

        const setItems = trainingSet.trainingSetItems || [];
        if (setItems.length === 0) return;

        const sortedSetItems = [...setItems].sort(
          (a, b) => a.orderNumber - b.orderNumber,
        );

        sortedSetItems.forEach((setItem, index) => {
          const dateData: IChartDataPoint = {
            date: trainingDate,
            planned: undefined,
            result: undefined,
            planned2: undefined,
            result2: undefined,
            showLabel: index === 0,
          };

          setItem.trainingSetItemOptions?.forEach((option) => {
            if (option.optionId === selectedOption?.id) {
              if (
                option.programValue !== null &&
                option.programValue !== undefined
              ) {
                dateData.planned = option.programValue;
              }
              if (option.value !== null && option.value !== undefined) {
                dateData.result = option.value;
              }
              if (
                option.programValue2 !== null &&
                option.programValue2 !== undefined
              ) {
                dateData.planned2 = option.programValue2;
              }
              if (option.value2 !== null && option.value2 !== undefined) {
                dateData.result2 = option.value2;
              }
            }
          });

          chartPoints.push(dateData);
        });
      });
    });

    const sortedChartPoints = chartPoints.sort((a, b) => {
      if (!a.date || !b.date) return 0;
      const dateA = new Date(a.date.split('.').reverse().join('-'));
      const dateB = new Date(b.date.split('.').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    });

    const seenDates = new Set<string>();
    const finalChartPoints = sortedChartPoints.map((point) => {
      if (point.date && !seenDates.has(point.date)) {
        seenDates.add(point.date);
        return { ...point, showLabel: true };
      }
      return { ...point, showLabel: false };
    });

    return finalChartPoints;
  }, [trainings, exerciseId, selectedOption]);

  const optionName = selectedOption?.name || '';
  const optionUnit = selectedOption?.unit;

  if (chartData.length === 0) {
    return null;
  }

  const formatValue = (value: number | undefined) => {
    if (value === undefined || value === null) return null;
    return Number(value.toFixed(2));
  };

  return (
    <Box className={classes.wrapper}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval="preserveStartEnd"
            tickFormatter={(value, index) => {
              const point = chartData[index];
              if (point?.showLabel === false) return '';
              if (index === 0) return value;
              const prevDate = chartData[index - 1]?.date;
              return prevDate === value ? '' : value;
            }}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            label={{
              value: optionUnit ? `${optionName} (${optionUnit})` : optionName,
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            formatter={(value: number) => formatValue(value)}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="planned"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ r: 3 }}
            name={t('statistics.chartPlanned')}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="result"
            stroke={theme.palette.success.main}
            strokeWidth={2}
            dot={{ r: 3 }}
            name={t('statistics.chartResult')}
            connectNulls
          />
          {splitBySide && (
            <>
              <Line
                type="monotone"
                dataKey="planned2"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                name="Planned (Side 2)"
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="result2"
                stroke={theme.palette.success.main}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                name="Result (Side 2)"
                connectNulls
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
